/**
 * "What is this exercise?" — the expandable description.
 *
 * Written for the person the app is otherwise useless to: someone handed a
 * plan that says "Bulgarian Split Squat 3 × 10" who has never seen one. The
 * text comes from the server's exercise library, so it's in the Dexie cache
 * with everything else and opens with no signal — which matters, because the
 * moment you need it is standing in front of a machine you don't recognise.
 *
 * Split into a button and a panel rather than one self-contained widget so the
 * caller owns which row is open. Every list this appears in already tracks "the
 * one open thing" (the queue's remove confirmation, the plan's open day), and
 * two descriptions expanded at once on a phone is just scrolling.
 *
 * Nothing renders at all when there's no description — a user's own invented
 * exercise has none, and neither does anything cached by a client older than
 * the column. A control that opens on an empty box is worse than no control.
 */

import type { Exercise } from '../db'

/** Whether there is anything to show. Callers gate the button on this. */
export function hasInfo(exercise: Exercise | null | undefined): boolean {
  return Boolean(exercise?.description)
}

/**
 * The toggle. Deliberately a "?" and not the exercise name: in the workout
 * queue the name is already a button that selects the exercise, and stealing
 * that tap to open a description would break the one action people take
 * mid-set.
 */
export function InfoButton({
  exercise,
  open,
  onToggle,
  style,
}: {
  exercise: Exercise
  open: boolean
  onToggle: () => void
  style?: React.CSSProperties
}) {
  return (
    <button
      className="secondary"
      aria-expanded={open}
      aria-label={open ? `Hide how to do ${exercise.name}`
                       : `How to do ${exercise.name}`}
      title="What is this exercise?"
      // Same reason as the remove button beside it: holding this inside a
      // draggable row must not start a drag.
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation()
        onToggle()
      }}
      style={{
        marginTop: '.3rem',
        // A thumb-sized target, matched to the × it sits next to.
        padding: '0 .8rem',
        minHeight: '2.4rem',
        fontSize: '1rem',
        lineHeight: 1,
        flex: '0 0 auto',
        ...style,
      }}
    >
      {open ? '×' : '?'}
    </button>
  )
}

/**
 * The description itself, plus the facts that are useful next to it: what it
 * loads and what it trains. Those come from columns that have always been
 * there, and they answer the follow-up question ("is this a machine or a
 * barbell?") without another tap.
 */
export function ExerciseInfoPanel({ exercise }: { exercise: Exercise }) {
  if (!exercise.description) return null

  const facts = [
    exercise.equipment,
    exercise.primary_muscle,
    exercise.is_unilateral ? 'one side at a time' : null,
  ].filter(Boolean)

  return (
    <div
      style={{
        margin: '.3rem 0 .1rem',
        padding: '.55rem .7rem',
        background: 'var(--surface-2)',
        borderRadius: 'var(--radius)',
        fontSize: '.85rem',
        lineHeight: 1.45,
      }}
    >
      <strong style={{ fontSize: '.8rem' }}>{exercise.name}</strong>
      <div style={{ marginTop: '.25rem' }}>{exercise.description}</div>
      {facts.length > 0 && (
        <div className="muted" style={{ fontSize: '.78rem', marginTop: '.35rem' }}>
          {facts.join(' · ')}
        </div>
      )}
    </div>
  )
}
